// Narrow HTTPS compatibility resolver, NOT a full ATW client. Authentication is
// the reader's responsibility: network callers must use a signed-proof reader.
import { parseAtUri } from "./recompute.ts";
import { COLLECTION } from "./resolve.ts";
import { assertRouteCollection, assertRouteName, validateRouteRecord, RouteValidationError } from "./route.ts";
import type { RepoReader } from "./types.ts";

export async function resolveNameRoute(
  reader: RepoReader, did: string, collection: string, name: string,
): Promise<{ target: string; routeCid: string }> {
  assertRouteCollection(collection);
  assertRouteName(name);
  const uri = `at://${did}/${collection}/${name}`;
  const record = await reader.getRecord(uri);
  if (record === null || record.uri !== uri) throw new RouteValidationError("named route absent or mismatched");
  const route = validateRouteRecord(record.value, collection);
  if (route.status === "retired" || route.alias !== undefined || route.targets?.length !== 1) {
    throw new RouteValidationError("HTTPS names require an active direct single-site route (no aliases)");
  }
  // This compatibility gateway cannot enforce ATW execution restrictions. Refuse
  // policy rather than silently widen it, including unknown noncritical fields.
  if (route.policy !== undefined && Object.keys(route.policy).length > 0) {
    throw new RouteValidationError("route policy requires an ATW client; unsupported on HTTPS names");
  }
  const target = route.targets[0]!.uri;
  const parsed = parseAtUri(target);
  if (parsed.did !== did || parsed.collection !== COLLECTION.site) {
    throw new RouteValidationError("HTTPS names require a site in the address publisher's repo");
  }
  return { target, routeCid: record.cid };
}
